<template>
    <!--
        Copyright Paul B. Lucas (C) 2023 - 2026   ALL RIGHTS RESERVED.
        Licensed to the University of Illinois Urbana Champaign

        This component allows a user to select an applicable org code which then may be used to index configs, tables or 
        other elements of the system which are keyed on org codes.

        A user may enter the org code directly, or use a set of hiearchical selectors for college, department, and 
        division.  These selectors are single selection (e.g., not multiple)

        Revisions:
            04-16-2024      pblucas     edited template for responsive rendering
            04-29-2024      pblucas     Modified org input to accept potentially multiple
                                        colliding org codes.

 
    -->
    <div class="master_container">
        <!-- what to view on basis of college code, department codes [] or org codes [] -->
        <div class="_r1_container">
            <!-- user can enter org code here, or use full selector -->
            <div class="_row" v-if="!bShowOrgList">
                <div class="_col_40">
                    <label class="_item_label" for="prop_1">Enter an orgcode</label>
                </div>
                <div class="_col_60">
                    <p class="_p_diminished_red" v-if="bInvalidCode">Invalid code entered</p>
                    <input id="prop_1" class="_input" type="text" v-model="codeSelection['orgcode']" 
                        tabindex="0" 
                        aria-label="input field for orgcode" 
                        aria-desribedby="input field if orgcode is already known" 
                        @keydown.enter.stop="submitCandidateCode"/>
                </div>
            </div>
            <div class="_row" v-if="bShowOrgList">
                <div class="_col_40">
                    <p class="_p_diminished_grn">&nbsp;</p>
                    <label class="_item_label" for="prop_2a">Select one</label>
                </div>
                <div class="_col_60">
                    <p class="_p_diminished_grn">Multiple codes found</p>
                    <select v-model="orgCodeItem" class="_select"  
                        tabindex="0" 
                        aria-label="selection dropdown box for division names and orgcodes " 
                        aria-describedby="selection dropdown to select division and orgcode where multiple orgcode found" 
                        @change="submitCandidateCode" autofocus >
                        <option id="prop_2a" class="_option_list" v-for="(item, idx) in aOrgList" :value="item"> {{item['org']}} - {{ item['name'] }}</option>
                    </select>
                </div>
            </div>
        </div>
        <div class="_r2_container">
            <p class="_p_info"><b>Or</b> use the selectors to find an org code for a specifc college, department, or division.  </p> 
        </div>
        <div class="_r3_container">
            <div class="_row">
                <div class="_col_40">
                    <label class="_item_label" for="prop_2">Select College Code</label>
                </div>
                <div class="_col_60">
                    <select v-model="codeSelection['college-code']" class="_select"  
                        tabindex="0"
                        aria-label="college code dropdown selector" 
                        aria-describedby="dropdown list of all college names and codes" 
                        @change="updateCollegeCodeSelection" autofocus  >
                        <!-- item['code'] is college code, item ['name'] is name of college -->
                        <!-- <option id="prop_2" class="_option_list" disabled> {{codeSelection['college-code']}} - {{ codeSelection['college'] }}</option>  -->
                        <option id="prop_2" class="_option_list" v-for="(item, idx) in aCollegeCodes" :value="item['code']"> {{item['code']}} - {{ item['name'] }}</option>
                    </select>

                </div>
            </div>
            <div class="_row" v-if="bShowDeptSelection">
                <div class="_col_40">
                    <label class="_item_label" for="prop_3">Select Department Code</label>
                </div>
                <div class="_col_60">
                    <select v-model="codeSelection['dept-code']" class="_select"  
                        tabindex="0" 
                        aria-label="selector for departments within a selected college" 
                        aria-describedby="dropdown selector of all departments within selected college" 
                        @change="updateDeptCodeSelection">
                        <!-- item['college-code'] is college code, item['dept-code'] is deptartment code, item['name'] is name of department -->
                        <!--  <option id="prop_3" class="_option_list" disabled> {{codeSelection['dept-code']}} - {{ codeSelection['department'] }}</option> -->
                        <option id="prop_3" class="_option_list" v-for="(item, idx) in aFilteredDeptCodes" :value="item['dept-code']"> {{item['dept-code']}} - {{ item['name'] }}</option>
                    </select>
                </div>
            </div>
            <div class="_row" v-if="bShowOrgSelection">
                <div class="_col_40">
                    <label class="_item_label" for="prop_4">Select Org Code</label>
                </div>
                <div class="_col_60">
                    <select id="prop4" v-model="codeSelection['orgcode']" class="_select" 
                        tabindex="0" 
                        aria-label="selector for divisions and orgcodes within selected department" 
                        aria-describedby="dropdown list of divisions and orgcodes within seleted department" 
                        @change="updateOrgCodeSelection">
                        <!-- item['dept-code'] is department code, item['org-code'] is org code, item['name'] is name of org/division -->
                        <!-- <option id="orgCodeOption" class="_option_list" disabled> {{codeSelection['orgcode']}} - {{ codeSelection['division'] }}</option> -->
                        <option id="orgCodeOption" class="_option_list" v-for="(item, idx) in aFilteredOrgCodes" :value="item['org']"> {{item['org']}} - {{ item['name'] }}</option>
                    </select>
                </div>
            </div>
        </div>
        
        <div class="_r4_container">
            <button class="_use_button" type="button" 
                tabindex="0" 
                aria-label="control to use the selected affiliation and proceed" 
                aria-describedby="control displays configuration sheet for selected affiliation" 
                @click.stop="submitCode(codeSelection)"
                @keydown.enter.stop="submitCode"><b>Use selections</b></button>
        </div>          
    </div>

  </template>
  
  <script>
        
        import {ref}            from 'vue'
        import Codes            from '@/configuration/uiuc.codes.json'
        import useArchiBusAPI   from '@/composables/useArchiBusAPI'
  
        export default {

            props: [
         
            ],
            emits: [
                'setCodes'        
            ],
            setup() {

                const { uiucCodeResults, makeUIUCCodes } = useArchiBusAPI()

                // select list sort options (developer preference)
                const bSortByName           =   ref(false)   // true sorts liste by 'name' | false by 'code';
                
                // set up the U of I system
                const uiucCodes             =   ref(Codes)
                const aCollegeCodes         =   ref({})
                const aDeptCodes            =   ref({})              // name[0], college-code[1], dept-code[2]
                const aOrgCodes             =   ref({})      
                makeUIUCCodes()
                .then( () => {
                    uiucCodes.value         =   uiucCodeResults.value
                    aCollegeCodes.value     =   uiucCodes.value['college-codes']
                    aDeptCodes.value        =   uiucCodes.value["dept-codes"]       // name[0], college-code[1], dept-code[2]
                    aOrgCodes.value         =   uiucCodes.value["org-codes"]        // name[0], dept-code[1], org[2]
                    
                    aCollegeCodes.value.sort((a, b) => {
                        var codeA
                        var codeB
                        if(bSortByName.value) {
                            codeA = a['name']
                            codeB = b['name']
                        }
                        else {
                            codeA = a['code']
                            codeB = b['code']
                        }                        
                        if (codeA < codeB) {
                            return -1
                        }
                        if (codeA > codeB) {
                            return 1
                        }
                        // codes must be equal
                        return 0
                    });
                })
 
                

                // filtered values for selection process
                const aFilteredDeptCodes    =   ref([])
                const aFilteredOrgCodes     =   ref([])
                const aOrgList              =   ref([])
                const aDeptList             =   ref([])
                const bInvalidCode          =   ref(false)

                const codeSelection         =   ref({
                    'college-code':         null,
                    'dept-code':            null,
                    'orgcode':              null
                })

                const orgCodeItem           =    ref({
                    'name':                 null,
                    'dept-code':            null,
                    'org':                  null
                })

 
                return {aCollegeCodes, aDeptCodes, aOrgCodes, aFilteredDeptCodes, aFilteredOrgCodes, aOrgList, 
                        aDeptList, orgCodeItem, codeSelection, bInvalidCode }
            },

            data () {
                return {

                    // sequencing of options
                    bShowDeptSelection: false,
                    bShowOrgSelection:  false,
                    bShowOrgList:       false,
                    selectedOrg:        null,
                    oMultipleItems:    {"org": "", "dept_code": "", "name": "Multiple Items Found"}
                    
                }
            },
          
            methods: {

                // required select
                updateCollegeCodeSelection() {
                    // filter departments based on whether the associated college code on the 
                    // department is that selected
                    this.aFilteredDeptCodes = this.aDeptCodes.filter ((item) => {return item['college-code'] == this.codeSelection['college-code']})
                    this.aFilteredDeptCodes.sort((a, b) => {
                        // check department preferences for sorts
                        var codeA
                        var codeB
                        if(this.bSortByName) {
                            codeA = a['name']
                            codeB = b['name']
                        }
                        else {
                            codeA = a['dept-code']
                            codeB = b['dept-code']
                        }               
                        if (codeA < codeB) {
                            return -1
                        }
                        if (codeA > codeB) {
                            return 1
                        }
                        // names must be equal
                        return 0
                    });
                    // toggle the dept selection form element
                    this.bShowDeptSelection = true
                },
                // required select
                updateDeptCodeSelection() {
                    // fires when user selects a department code from list
                    // filter orgs based on selected department code
                    // aSelectedDepts is array of dept codes
                    //
                    this.aFilteredOrgCodes = this.aOrgCodes.filter( c => c['dept-code'] === this.codeSelection['dept-code'])
                    this.aFilteredOrgCodes.sort((a, b) => {
                        var codeA
                        var codeB
                        if(this.bSortByName) {
                            codeA = a['name']
                            codeB = b['name']
                        }
                        else {
                            var codeA = a['org']
                            var codeB = b['org']
                        }                        
                        if (codeA < codeB) {
                            return -1
                        }
                        if (codeA > codeB) {
                            return 1
                        }
                        // names must be equal
                        return 0
                    });
                    // toggle the org code selection
                    this.bShowOrgSelection = true
                    
                },
                updateOrgCodeSelection()    {
                    // reserved for potential action
                },
                // Function:  A three/six digit code is submitted for checking.  Since they may 
                // not be unique, search the OrgCode table for matching codes and present user with a list.

                submitCandidateCode () {
                    // trim potential 6 digit codes to three
                    this.codeSelection.orgcode = this.codeSelection.orgcode.substring(0,3)

                    if(this.bShowOrgList) {
                        // user submitted from list - proceed 
                        // clarify the affiliation by adding department code
                        this.codeSelection['orgcode'] = this.orgCodeItem['org']
                        this.codeSelection['dept-code'] = this.orgCodeItem['dept-code']
                        this.submitCode()
                    }
                    else {
                        // list not shown - user entered potential org
                        this.aOrgList = this.aOrgCodes.filter( (item) =>    {
                            return item.org == this.codeSelection.orgcode
                        })
                        if(this.aOrgList.length > 1)     {
                            // more than one affiliation on this orgcode - user must select
                            this.orgCodeItem = this.oMultipleItems
                            this.bShowOrgList = true
                        }
                        else if (this.aOrgList.length == 0) {
                            // invalid org
                            this.bInvalidCode = true
                            this.codeSelection['orgcode'] = ""
                        }
                        else {
                            // only one orgcode - is unique but still must include department code
                            this.codeSelection['dept-code'] = this.aOrgList[0]['dept-code']
                            this.submitCode()
                        }
                    }
                },
                submitCode()    {
                    // pass component copy of codes up to parent
                    //console.log("OrgSelector:: submitCode: fired")

                    this.$emit( "setCodes", this.codeSelection)

                }
           

            }, // end of methods
    
            // lifecycle hooks
            beforeMount() {
                
            } 
            
        }
  
  </script>
  
  <style scoped>
        .master_container {
            width: 60%;
            background-color: #BFC9D9;
            font-size: 1.0em;
            margin: 10px auto 0;
            padding: 5px;
            border-style: solid;
            border-width: 2px;
            border-color: #13294B;  
        }     
        ._p_info     {
            max-width:100%;
            text-align: left;
            color: #13294B;
            margin: 0;
            padding: 5px;
        }
        ._p_diminished_grn     {
            max-width:100%;
            font-size: 0.8em;
            text-align: left;
            color: #006230;
            margin: 0;
            padding: 5px;
        }
        ._p_diminished_red {
            max-width:100%;
            font-size: 0.8em;
            text-align: left;
            color: #FF5F05;
            margin: 0;
            padding: 5px; 
        }
        ._item_label {
            width: 100%;
            font-size: 0.9em;
            font-weight: bold;
            color: #13294B;
            margin: 0;
            padding: 5px;
            text-align: right;
        }
        ._select {
            order: 2;  
            min-width: 35%;
            background-color:#a4b9dc;
            color: black;
        }
        ._option_list   {
            color: black;
            margin: 5px 0 5px;
            letter-spacing: 1px;
            font-weight: normal;
            font-size: 0.8em;
        }
        ._use_button {
            color: white; 
            text-align: center;
            background-color: #13294B;
            border-style: solid;
            border-color: #13294B;
            border-radius: 5px;
            border-width: 1px;
             padding: 2px 10px;
        }
        ._use_button:hover {
            background-color: #FF5F05;
            color: white;
        }
        ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
        }
        ._main_button:hover {
            background-color: #FF5F05;
            color: white;
        }
 

        /*  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        /   RESPONSIVE DESIGN STYLING
        >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>/*
        /*  responsive design flex rows  */
        ._r1_container, ._r2_container, ._r3_container {
            width: 100%;
            text-align: left;
            margin: 5px 0 0 0;
            padding: 0;
        }
        /* override for buttons */
        ._r4_container {
            width: 100%;
            text-align: center;
            margin: 20px auto 10px;
            padding: 0;
        }
        
        /*  responive design columns - scol are subcolumns  */
        ._row:after {
            content: "";
            display: table;
            clear: both;
        }
        ._col_35 {
            float: left;
            width: 35%;
            margin: 0;
            padding: 2px 0;
        }
        ._col_40 {
            float: left;
            width: 40%;
            margin: 0;
            padding: 2px 0;
        }
        ._col_60 {
            float: left;
            width: 60%;
            margin: 0;
            padding: 2px 0;
        }
        ._col_65 {
            float: left;
            width: 65%;
            margin: 0;
            padding: 2px 0 ;
        }
        

        @media screen and (max-width: 800px) {
            ._col_35  {
                width: 100%;
                margin-top: 0; 
                padding: 2px 0;
            }
            ._col_40 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
            ._col_60 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
            ._col_65 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
        }
    
        @media screen and (max-width: 620px) {
            ._col_35  {
                width: 100%;
                margin-top: 0; 
                padding: 2px 0;
            }
            ._col_40 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
            ._col_60 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
            ._col_65 {
                width: 100%;
                margin: 0;
                padding: 2px 0;
            }
        }

  </style>
  